import {
  AfterContentChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Observable, forkJoin, map, startWith } from "rxjs";
import {
  CebePalViewModel,
  TipoRiesgoSocialViewModel,
} from "src/app/_core/models/centros/cebe-pal.model";
import { CebeViewModel } from "src/app/_core/models/centros/cebe.model";
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";
import { AuthService } from "src/app/_core/providers/auth.service";
import { CebePalService } from "src/app/_core/providers/cebe-pal.service";
import { DecimalService } from "src/app/_core/providers/decimals.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { CebePageComponent } from "../../pages/cebe/cebe.page";
@Component({
  selector: "app-cebe-pal-card",
  templateUrl: "./cebe-pal-card.component.html",
  styleUrls: ["./cebe-pal-card.component.scss"],
})
export class CebePalCardComponent implements OnInit, AfterContentChecked {
  // Parameters
  @Input() pal: CebePalViewModel;
  @Input() cebe: CebeViewModel;
  @ViewChild("nombreInputPal", { static: true }) nombreInputPal: ElementRef;
  @Input() parent: CebePageComponent;
  @Output() palFormComplete = new EventEmitter<boolean>();

  public employees: EmpleadoViewModel[] = [];
  public socialRisks: TipoRiesgoSocialViewModel[] = [];
  employeesfilter: Observable<EmpleadoViewModel[]>;
  public photo: any = "/assets/images/user.png";
  employeeControl: FormControl = new FormControl("");
  myFormPal: FormGroup;

  countControls: number = 0;
  countFilled: number = 0;

  constructor(
    private readonly _masterService: MasterService,
    public _translateService: TranslateService,
    private readonly _notificationService: NotificationService,
    private readonly _authService: AuthService,
    private readonly _cebePalService: CebePalService,
    private readonly fb: FormBuilder,
    private readonly renderer: Renderer2,
    private readonly el: ElementRef,
    public decimalService: DecimalService
  ) {}

  // Events
  ngOnInit(): void {
    this._loadData();
    this._loadForm();
  }

  triggerFormSubmission(): boolean {
    return this.onSubmit();
  }

  onSubmit(): boolean {
    if (this.myFormPal.valid) {
      // let formData = this.myFormPal.value as CebePalViewModel;
      let formData = this.mapFormValuesToCebePalModel(this.myFormPal.value);

      if (formData.id !== 0) {
        this._cebePalService.updateCebePal(formData).subscribe((pal) => {
          this.pal = pal;
          this._notificationService.showSuccess(
            this._translateService.instant("messages.savedSuccessfully"),
            null
          );
        });
      } else {
        this.myFormPal.get("id")?.setValue(0);
        this.myFormPal.get("cebeID")?.setValue(this.cebe?.id);

        formData = this.mapFormValuesToCebePalModel(this.myFormPal.value);
        this._cebePalService.createCebePal(formData).subscribe((pal) => {
          this.pal = pal;
          this._notificationService.showSuccess(
            this._translateService.instant("messages.savedSuccessfully"),
            null
          );
        });
      }
      return true;
    } else {
      this.markFormGroupTouched(this.myFormPal);
      this._notificationService.showError(
        `${this._translateService.instant("cebe.generalData.palData.title") 
          } - ${ 
          this._translateService.instant("messages.validationErrors")}`
      );
      return false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ("pal" in changes) {
      this._loadData();
      this._loadForm();
    }
  }

  ngAfterContentChecked(): void {
    this.getFormStatus();
  }

  private mapFormValuesToCebePalModel(formValues: any): CebePalViewModel {
    return {
      id: formValues.id || 0,
      cebeID: formValues.cebeID || 0,
      disponeInfo: formValues.disponeInfo || false,
      responsableOfertaID: this.employeeControl.value?.id || null,
      ofertaAdjudica: formValues.ofertaAdjudica || null,
      fechaAdjudica: formValues.fechaAdjudica || null,
      gis: formValues.gis || false,
      riesgoSocialID: formValues.gis ? formValues.riesgoSocialID : null,
      presupuestoAsignado: formValues.gis
        ? formValues.presupuestoAsignado
        : null,
    };
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private _loadData(): void {
    forkJoin([
      this._masterService.getEmpleados(),
      this._masterService.getTipoRiesgoSocialList(),
    ]).subscribe(([s1, s2]) => {
      this.employees = s1;
      this.socialRisks = s2;

      const empleado: EmpleadoViewModel = this.employees.find(
        (f) => f.id == this.pal?.responsableOfertaID
      );

      if (empleado) {
        this.employeeControl.setValue(empleado);
        this.getPhoto(empleado?.email);
      }
    });
  }

  private _loadForm(): void {
    this.myFormPal = this.fb.group({
      id: [this.pal?.id ?? 0],
      cebeID: [this.cebe?.id],
      disponeInfo: [this.pal?.disponeInfo],
      responsableOferta: this.employeeControl,
      ofertaAdjudica: [this.pal?.ofertaAdjudica ?? null],
      fechaAdjudica: [this.pal?.fechaAdjudica ?? null],
      gis: [this.pal?.gis],
      riesgoSocialID: [this.pal?.riesgoSocialID ?? null],
      presupuestoAsignado: [this.pal?.presupuestoAsignado ?? null],
    });

    this.myFormPal.patchValue(this.pal);
    this.showGIS();

    this.employeesfilter = this.employeeControl.valueChanges.pipe(
      startWith(""),
      map((value) => (typeof value === "string" ? value : value?.nombre)),
      map((nombre) => (nombre ? this._filter(nombre) : this.employees.slice()))
    );

    this.myFormPal.get("gis")?.valueChanges.subscribe(() => {
      this.showGIS();
    });

    this.myFormPal.valueChanges.subscribe(() => {
      this.getFormStatus();
    });
  }

  getFormStatus() {
    this.countControls = 0;
    this.countFilled = 0;

    Object.keys(this.myFormPal.controls).forEach((controlName) => {
      if (controlName !== "cebeID" && controlName !== "id") {
        const control = this.myFormPal.get(controlName);

        const controlElement = document.querySelector(
          `[formControlName="${controlName}"]`
        );

        if (controlElement) {
          const hasHiddenClass =
            controlElement.parentElement.classList.contains("hidden");

          if (!hasHiddenClass) {
            this.countControls++;

            if (control?.value !== null && control?.value !== "") {
              this.countFilled++;
            }
          }
        }
      }
    });

    if (this.countFilled !== 0) {
      this.palFormComplete.emit(this.countControls == this.countFilled);
    }
  }

  private _filter(nombre: string): EmpleadoViewModel[] {
    const filterValue = nombre.toLowerCase();
    return this.employees.filter((option) =>
      option.nombre.toLowerCase().includes(filterValue)
    );
  }

  displayFn(empleado?: EmpleadoViewModel): string | undefined {
    return empleado ? empleado.nombre : undefined;
  }

  onOptionSelected(event) {
    const empleado = event.option.value;
    this.nombreInputPal.nativeElement.value = empleado.nombre;
    this.getPhoto(empleado.email);
  }

  public getPhoto(email: string) {
    this._authService.getUserPhoto(email).subscribe(
      (photoResult) => {
        const reader = new FileReader();
        reader.addEventListener(
          "load",
          () => {
            if (reader && reader.result) {
              this.photo = reader.result;
            } else {
              this.photo = "/assets/images/user.png";
            }
          },
          false
        );

        if (photoResult) {
          reader.readAsDataURL(photoResult);
        }
      },
      (error) => {
        this.photo = "/assets/images/user.png";
      }
    );
  }

  showGIS() {
    const checkedPM = this.myFormPal.get("gis")?.value;

    if (checkedPM) {
      this.toggleVisibility(".instSocialRisks", "hidden", false);
      this.toggleVisibility(".instBudgetAssigned", "hidden", false);
    } else {
      this.toggleVisibility(".instSocialRisks", "hidden", true);
      this.toggleVisibility(".instBudgetAssigned", "hidden", true);
    }
  }

  private toggleVisibility(
    selector: string,
    className: string,
    add: boolean
  ): void {
    const elements = this.el.nativeElement.querySelectorAll(selector);
    elements.forEach((element: HTMLElement) => {
      if (add) {
        this.renderer.addClass(element, className);
      } else {
        this.renderer.removeClass(element, className);
      }
    });
  }
}
